/* ============================================================
   apiClient.js — the one HTTP entry point.

   Attaches the bearer token, renews proactively inside the skew window, and
   retries exactly once on a 401. Never loops: if the retry also 401s, the
   session is over and the expired handler fires.
   ============================================================ */

import { ApiError, parseDetail, SessionExpiredError } from './errors'
import { getAccessToken, getPreviewRoleId, getPreviewUserId, isExpiring } from './tokenStore'
import { refreshSession } from './refresh'
import { claimArmedDraft, recordFailedDraft, requestRestore } from './formRecovery'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

// AuthProvider registers this so the module never has to import the router.
let onSessionExpired = () => {}
export function setSessionExpiredHandler(fn) {
  onSessionExpired = fn
}

// ToastProvider registers this; the client stays free of React imports.
let onRequestAlert = () => {}
export function setRequestAlertHandler(fn) {
  onRequestAlert = fn
}

/* Failure policy. A GET that never reached the server (network error) or that
   the server answered with a 5xx is tried RETRY_ATTEMPTS times in total,
   RETRY_DELAY_MS apart, with an alert on every failure. The wait is a plain
   timer, so the rest of the page keeps working; only the awaiting caller sees
   the delay. Writes are never re-sent: one whose request reached the server
   before the connection dropped would land twice. They fail on the first
   attempt with the final alert and the user re-submits. 4xx is the caller's
   problem and is never retried. A `quiet` request (an optional integration
   that may be unconfigured on purpose) skips both: one attempt, no alert. */
const RETRY_ATTEMPTS = 3
const RETRY_DELAY_MS = 20_000

/* Deploys: the API restarts (~30s) while the page stays up, so data GETs get
   "server unreachable" or an nginx 502/503 (an HTML page, unlike our app's own
   JSON errors). Not 504: that can be a slow request nginx gave up on, and retrying
   would run it again. Those are retried quickly and quietly instead: every
   GATEWAY_RETRY_MS for up to GATEWAY_WINDOW_MS, with no alert for the first
   GATEWAY_QUIET_MS, so a deploy usually goes unnoticed. */
const GATEWAY_WINDOW_MS = 30_000
const GATEWAY_RETRY_MS = 3_000
const GATEWAY_QUIET_MS = 10_000

function isGatewayFailure(res) {
  if (res == null) return true
  if (![502, 503].includes(res.status)) return false
  return !(res.headers.get('content-type') || '').includes('application/json')
}

// API prefix -> what the user calls that part of the app.
const MODULE_LABELS = {
  analytics: 'analytics', reports: 'reports', inbox: 'the inbox', email: 'the inbox',
  job: 'jobs', jobs: 'jobs', candidate: 'candidates', pipeline: 'the pipeline',
  interview: 'interviews', feedback: 'feedback', offers: 'offers', tasks: 'tasks',
  assessments: 'assessments', notifications: 'notifications', users: 'your account',
  roles: 'roles', permissions: 'permissions', department: 'departments', forms: 'forms',
  talent: 'talent search', s3: 'files', sheet: 'sheet imports', activity: 'activity',
  search: 'search', 'org-settings': 'settings', 'verification-check': 'verification checks',
}

/** "/analytics/kpis/fetch" -> "analytics": the area named in failure alerts. */
function moduleName(path) {
  const seg = String(path).split('?')[0].split('/').filter(Boolean)[0] || ''
  return MODULE_LABELS[seg] || (seg ? seg.replace(/[-_]+/g, ' ') : 'this page')
}

/* One failing endpoint polled by several widgets — or several endpoints of the
   same area failing together — used to stack identical toasts. An alert whose
   text was shown in the last ALERT_DEDUPE_MS is dropped. */
const ALERT_DEDUPE_MS = 15_000
const recentAlerts = new Map()
function raiseAlert(message, tone, title, opts) {
  const key = `${tone}|${title}|${message}`
  const now = Date.now()
  if (now - (recentAlerts.get(key) ?? 0) < ALERT_DEDUPE_MS) return
  recentAlerts.set(key, now)
  for (const [k, at] of recentAlerts) if (now - at >= ALERT_DEDUPE_MS) recentAlerts.delete(k)
  onRequestAlert(message, tone, title, opts)
}

function wait(ms, signal) {
  return new Promise((resolve, reject) => {
    const abortError = () => new DOMException('Aborted', 'AbortError')
    if (signal?.aborted) return reject(abortError())
    const onAbort = () => {
      clearTimeout(timer)
      reject(abortError())
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/* Plain-language copy only: no status codes, no server text. `status` stays
   in the signature for whoever extends this; the Network tab has the detail. */
function alertFailure({ kind, method, path, attempt, draft }) {
  const offline = kind !== 'server'
  if (method !== 'GET') {
    // A write whose form armed a draft gets a way back to that form, filled in.
    if (draft) recordFailedDraft(draft)
    const action = draft ? { label: 'Reopen form', onClick: requestRestore } : undefined
    raiseAlert(
      offline
        ? 'Your changes weren’t saved because we couldn’t reach the server. Check your connection and try again.'
        : 'Your changes weren’t saved because of a problem on our side. Please try again in a moment.',
      'error', 'Couldn’t save', { action },
    )
    return
  }
  const area = moduleName(path)
  const last = attempt >= RETRY_ATTEMPTS
  if (!last) {
    raiseAlert(
      offline
        ? 'We couldn’t reach the server. Trying again automatically…'
        : `We’re having trouble loading ${area}. Trying again automatically…`,
      'warning', offline ? 'Connection problem' : 'Taking longer than usual',
    )
    return
  }
  raiseAlert(
    offline
      ? 'We still can’t reach the server. Check your internet connection, then refresh the page.'
      : `${area.charAt(0).toUpperCase() + area.slice(1)} isn’t available right now. Please try again in a few minutes — if it keeps happening, contact support.`,
    'error', offline ? 'You appear to be offline' : 'Couldn’t load data',
  )
}

/**
 * Runs `send` under the failure policy above. Resolves with the final Response
 * (possibly a 5xx after the last attempt) and whether the user was already
 * alerted about it; throws the network ApiError when every attempt failed to
 * connect. AbortError propagates immediately, including mid-wait.
 */
async function sendWithRetry(send, { method, path, signal, draft, quiet }) {
  const started = Date.now()
  for (let attempt = 1; ; attempt += 1) {
    let res = null
    try {
      res = await send()
    } catch (err) {
      if (err?.name === 'AbortError') throw err
    }
    const kind = res == null ? 'network' : res.status >= 500 ? 'server' : null
    if (!kind) return { res, alerted: false }
    if (quiet) {
      if (res) return { res, alerted: false }
      throw new ApiError('Unable to reach the server. Check your connection.', 0, null)
    }

    if (method === 'GET' && isGatewayFailure(res)) {
      const elapsed = Date.now() - started
      if (elapsed + GATEWAY_RETRY_MS <= GATEWAY_WINDOW_MS) {
        if (elapsed >= GATEWAY_QUIET_MS) alertFailure({ kind, status: res?.status, method, path, attempt: 1, draft })
        await wait(GATEWAY_RETRY_MS, signal)
        continue
      }
      attempt = RETRY_ATTEMPTS // window used up: final alert below, no further tries
    }

    alertFailure({ kind, status: res?.status, method, path, attempt, draft })
    if (method !== 'GET' || attempt >= RETRY_ATTEMPTS) {
      if (res) return { res, alerted: true }
      const err = new ApiError('Unable to reach the server. Check your connection.', 0, null)
      err.alerted = true
      throw err
    }
    await wait(RETRY_DELAY_MS, signal)
  }
}

/* "View as role" / "View as user". `preview` undefined -> this tab's stored
   role; null/false -> suppress stored role AND stored user (unless previewUser
   is an explicit id). `previewUser` undefined -> stored user (unless suppressed);
   null/false -> none; an id -> that user (validating a pick before it is stored).
   Only sent with a bearer. Both headers at once are a 400. */
function setPreviewHeader(headers, preview, previewUser) {
  if (!headers.Authorization) return
  const suppressStored = preview === null || preview === false
  const roleId = suppressStored ? null : preview === undefined ? getPreviewRoleId() : preview || null
  const userId = previewUser !== undefined
    ? previewUser || null
    : suppressStored
      ? null
      : getPreviewUserId()
  if (roleId != null) headers['X-Preview-Role'] = String(roleId)
  if (userId != null) headers['X-Preview-User'] = String(userId)
}

function buildUrl(path, params) {
  // An absolute URL (a call pinned to another host) skips API_BASE.
  const full = /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`
  const url = new URL(full, window.location.origin)
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      // An array repeats the key (?status=a&status=b) — FastAPI's list[str] Query.
      if (Array.isArray(v)) {
        v.filter((x) => x !== undefined && x !== null && x !== '').forEach((x) => url.searchParams.append(k, x))
      } else if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v)
    }
  }
  return url.toString()
}

/**
 * @param {object} opts
 * @param {boolean} [opts.auth=true]  attach the session bearer token
 * @param {string}  [opts.token]      an explicit token (the password-reset flow);
 *                                    suppresses all refresh behaviour
 * @param {number|null|false} [opts.preview]  view-as-role override, see setPreviewHeader
 * @param {string|null|false} [opts.previewUser]  view-as-user override, see setPreviewHeader
 * @param {RequestCredentials} [opts.credentials]  fetch credentials mode; login/signup
 *                                    pass 'include' so the refresh cookie is stored
 * @param {boolean} [opts.quiet]      no failure alerts and no 5xx/network retries
 * @param {boolean} [opts.keepalive]  let the request outlive the page (tab close)
 */
export async function request(
  path,
  { method = 'GET', body, params, auth = true, token, signal, preview, previewUser, credentials, quiet, keepalive } = {},
) {
  // Before any await: the draft a form armed belongs to the write it just sent.
  const draft = method !== 'GET' ? claimArmedDraft() : null

  // PROACTIVE renewal. Coalesced by single-flight, so a screen firing six
  // queries at once on an expiring token still triggers exactly one refresh.
  if (auth && !token && isExpiring()) {
    try {
      await refreshSession()
    } catch {
      // Fall through — the 401 path below makes the final call. This keeps a
      // transient network blip from logging the user out.
    }
  }

  // Multipart uploads pass a FormData body. Content-Type is deliberately NOT
  // set for those: the browser has to write it itself so the generated boundary
  // token ends up in the header, and a hand-set value strips it and the server
  // reads an unparseable body. FormData is replayable, so the 401 retry below
  // can re-send the same object.
  const multipart = typeof FormData !== 'undefined' && body instanceof FormData

  const send = async () => {
    const headers = { Accept: 'application/json' }
    if (body != null && !multipart) headers['Content-Type'] = 'application/json'
    const bearer = token ?? (auth ? getAccessToken() : null)
    if (bearer) headers.Authorization = `Bearer ${bearer}`
    if (!token) setPreviewHeader(headers, preview, previewUser)
    return fetch(buildUrl(path, params), {
      method,
      headers,
      signal,
      credentials,
      keepalive,
      body: body == null ? undefined : multipart ? body : JSON.stringify(body),
    })
  }

  let { res, alerted } = await sendWithRetry(send, { method, path, signal, draft, quiet })

  // REACTIVE renewal — exactly one retry.
  if (res.status === 401 && auth && !token) {
    try {
      await refreshSession()
    } catch (err) {
      if (err instanceof SessionExpiredError) onSessionExpired()
      throw err
    }
    res = await send()
    alerted = false
    if (res.status === 401) {
      // A fresh access token still 401s: the user was deactivated or soft-deleted
      // server-side (get_current_user rejects both). Signing them out is correct.
      onSessionExpired()
      throw new ApiError('Session expired', 401, null)
    }
  }

  let data = null
  const text = await res.text()
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = null
    }
  }

  if (!res.ok) {
    const err = new ApiError(
      parseDetail(data?.detail) || res.statusText || 'Request failed',
      res.status,
      data,
    )
    // Callers skip their own error toast when the retry policy already raised one.
    err.alerted = alerted
    throw err
  }
  return data
}

/**
 * Authenticated file download. Document routes return a binary body, so they
 * cannot go through `request()` (that always JSON-parses). Same bearer /
 * refresh behaviour as `request`; 404s stay 404 (the download route never 403s).
 */
export async function downloadFile(path, { params, auth = true, filename } = {}) {
  if (auth && isExpiring()) {
    try {
      await refreshSession()
    } catch {
      /* fall through — the 401 path below makes the final call */
    }
  }

  const send = async () => {
    const headers = { Accept: '*/*' }
    const bearer = auth ? getAccessToken() : null
    if (bearer) headers.Authorization = `Bearer ${bearer}`
    setPreviewHeader(headers)
    return fetch(buildUrl(path, params), { method: 'GET', headers })
  }

  let { res, alerted } = await sendWithRetry(send, { method: 'GET', path })

  if (res.status === 401 && auth) {
    try {
      await refreshSession()
    } catch (err) {
      if (err instanceof SessionExpiredError) onSessionExpired()
      throw err
    }
    res = await send()
    alerted = false
    if (res.status === 401) {
      onSessionExpired()
      throw new ApiError('Session expired', 401, null)
    }
  }

  if (!res.ok) {
    let data = null
    const text = await res.text()
    if (text) {
      try {
        data = JSON.parse(text)
      } catch {
        data = null
      }
    }
    const err = new ApiError(
      parseDetail(data?.detail) || res.statusText || 'Download failed',
      res.status,
      data,
    )
    err.alerted = alerted
    throw err
  }

  const blob = await res.blob()
  const fromHeader = filenameFromDisposition(res.headers.get('Content-Disposition'))
  const name = filename || fromHeader || 'download'
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * Authenticated binary GET returning an object URL for <img>/<video>/<iframe>
 * use — a plain src attribute cannot carry the bearer token. Returns null on
 * 404 (the resource legitimately not existing, e.g. a job with no cover
 * image). `type` re-wraps the blob with that MIME type: routes that send
 * application/octet-stream would otherwise trigger a download instead of the
 * browser's inline viewer. Callers own the URL: revoke it when done.
 */
export async function fetchBlobUrl(path, { params, auth = true, type } = {}) {
  if (auth && isExpiring()) {
    try {
      await refreshSession()
    } catch {
      /* fall through — the 401 path below makes the final call */
    }
  }

  const send = async () => {
    const headers = { Accept: '*/*' }
    const bearer = auth ? getAccessToken() : null
    if (bearer) headers.Authorization = `Bearer ${bearer}`
    setPreviewHeader(headers)
    return fetch(buildUrl(path, params), { method: 'GET', headers })
  }

  let { res, alerted } = await sendWithRetry(send, { method: 'GET', path })

  if (res.status === 401 && auth) {
    try {
      await refreshSession()
    } catch (err) {
      if (err instanceof SessionExpiredError) onSessionExpired()
      throw err
    }
    res = await send()
    alerted = false
    if (res.status === 401) {
      onSessionExpired()
      throw new ApiError('Session expired', 401, null)
    }
  }

  if (res.status === 404) return null
  if (!res.ok) {
    const err = new ApiError(res.statusText || 'Request failed', res.status, null)
    err.alerted = alerted
    throw err
  }
  const blob = await res.blob()
  return URL.createObjectURL(type ? new Blob([blob], { type }) : blob)
}

function filenameFromDisposition(header) {
  if (!header) return null
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (star) {
    try {
      return decodeURIComponent(star[1])
    } catch {
      return star[1]
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header)
  if (quoted) return quoted[1]
  const plain = /filename=([^;]+)/i.exec(header)
  return plain ? plain[1].trim() : null
}

export const get = (path, params, opts) => request(path, { ...opts, params })
export const post = (path, body, opts) => request(path, { ...opts, method: 'POST', body })
export const put = (path, body, opts) => request(path, { ...opts, method: 'PUT', body })
export const del = (path, opts) => request(path, { ...opts, method: 'DELETE' })
